import { UserActivityCaps } from "../schema/userActivity";

/**
 * The rows of the User Deep Dive grid, in the order the mockup lists them: each channel's
 * cap, then what was sent against it, then what came back.
 */

export interface UserActivityRowConfig {
	/** Key into the grid's `metric → day → count` map. Absent for a cap row. */
	metric?: string;
	/** i18n key under `analytics.users.rows.*` */
	labelKey: string;
	/** Renders the user's daily cap instead of a per-day count. */
	capField?: keyof UserActivityCaps;
	/** Shows each day's value as a share of this cap. */
	shareOfCap?: keyof UserActivityCaps;
	/** Indents the row as a response to the send above it. */
	isEngagement?: boolean;
}

export interface UserActivityRowGroup {
	key: "linkedin" | "email";
	labelKey: string;
	rows: UserActivityRowConfig[];
}

/** Past days: what went out, and what came back. */
export const SENT_ROW_GROUPS: UserActivityRowGroup[] = [
	{
		key: "linkedin",
		labelKey: "analytics.users.groups.linkedin",
		rows: [
			{ labelKey: "analytics.users.rows.maxConnections", capField: "linkedinInvites" },
			{
				metric: "linkedinInvites",
				labelKey: "analytics.users.rows.sentConnections",
				shareOfCap: "linkedinInvites",
			},
			{
				metric: "linkedinAccepted",
				labelKey: "analytics.users.rows.acceptedConnections",
				isEngagement: true,
			},
			{ labelKey: "analytics.users.rows.maxMessages", capField: "linkedinMessages" },
			{
				metric: "linkedinMessages",
				labelKey: "analytics.users.rows.sentMessages",
				shareOfCap: "linkedinMessages",
			},
			{
				metric: "linkedinReplies",
				labelKey: "analytics.users.rows.linkedinReplies",
				isEngagement: true,
			},
			{
				metric: "linkedinPositiveReplies",
				labelKey: "analytics.users.rows.linkedinPositiveReplies",
				isEngagement: true,
			},
		],
	},
	{
		key: "email",
		labelKey: "analytics.users.groups.email",
		rows: [
			{ labelKey: "analytics.users.rows.maxEmails", capField: "emails" },
			{ metric: "emails", labelKey: "analytics.users.rows.sentEmails", shareOfCap: "emails" },
			{ metric: "emailsOpened", labelKey: "analytics.users.rows.emailsOpened", isEngagement: true },
			{
				metric: "emailsClicked",
				labelKey: "analytics.users.rows.emailsClicked",
				isEngagement: true,
			},
			{
				metric: "emailsReplied",
				labelKey: "analytics.users.rows.emailsReplied",
				isEngagement: true,
			},
			{
				metric: "emailsPositiveReplies",
				labelKey: "analytics.users.rows.emailsPositiveReplies",
				isEngagement: true,
			},
			{
				metric: "emailsBounced",
				labelKey: "analytics.users.rows.emailsBounced",
				isEngagement: true,
			},
		],
	},
];

/**
 * Future days: caps and what is queued against them. No engagement rows — nothing has
 * been sent yet, so a "replies" row would read zero for every column and imply failure.
 */
export const SCHEDULED_ROW_GROUPS: UserActivityRowGroup[] = [
	{
		key: "linkedin",
		labelKey: "analytics.users.groups.linkedin",
		rows: [
			{ labelKey: "analytics.users.rows.maxConnections", capField: "linkedinInvites" },
			{
				metric: "linkedinInvites",
				labelKey: "analytics.users.rows.scheduledConnections",
				shareOfCap: "linkedinInvites",
			},
			{ labelKey: "analytics.users.rows.maxMessages", capField: "linkedinMessages" },
			{
				metric: "linkedinMessages",
				labelKey: "analytics.users.rows.scheduledMessages",
				shareOfCap: "linkedinMessages",
			},
		],
	},
	{
		key: "email",
		labelKey: "analytics.users.groups.email",
		rows: [
			{ labelKey: "analytics.users.rows.maxEmails", capField: "emails" },
			{
				metric: "emails",
				labelKey: "analytics.users.rows.scheduledEmails",
				shareOfCap: "emails",
			},
		],
	},
];
